import { useState } from 'react';
import { contactsAPI } from '../api/contacts';
import { Mail, Phone, MapPin, Send, Check, AlertCircle, Clock, Globe } from 'lucide-react';
import Notification from '../components/common/Notification';
import SeoMeta from '../components/SeoMeta';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    projectType: '',
    budget: '',
    timeline: ''
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Le sujet est requis';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    } else if (formData.message.length < 20) {
      newErrors.message = 'Le message doit contenir au moins 20 caractères';
    }
    
    if (formData.phone && !/^[0-9+\-\s]{10,15}$/.test(formData.phone)) {
      newErrors.phone = 'Numéro de téléphone invalide';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showNotification('error', 'Veuillez corriger les erreurs dans le formulaire');
      return;
    }
    
    setLoading(true);
    
    try {
      await contactsAPI.create(formData);
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        projectType: '',
        budget: '',
        timeline: ''
      });
      
      setSubmitted(true);
      showNotification('success', 'Message envoyé avec succès !');
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Erreur lors de l\'envoi du message. Veuillez réessayer.';
      
      showNotification('error', errorMessage);
      console.error('Error submitting contact form:', error);
      
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {notification && (
          <Notification
            type={notification.type}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}
        
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-200">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center border-2 border-green-200">
                  <Check size={48} className="text-green-700" />
                </div>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
                Message envoyé avec succès !
              </h2>
              
              <p className="text-lg text-gray-800 text-center mb-8 font-medium">
                Merci pour votre message. Nous vous contacterons dans les plus brefs délais.
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
                <p className="font-bold text-green-900 mb-4 text-lg flex items-center gap-2">
                  <Check size={20} className="text-green-700" />
                  Notre engagement :
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center p-3 bg-white rounded-lg border border-green-100">
                    <Check size={20} className="text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-gray-800 font-medium">Réponse sous 24h ouvrées</span>
                  </li>
                  <li className="flex items-center p-3 bg-white rounded-lg border border-green-100">
                    <Check size={20} className="text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-gray-800 font-medium">Étude gratuite de votre projet</span>
                  </li>
                  <li className="flex items-center p-3 bg-white rounded-lg border border-green-100">
                    <Check size={20} className="text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-gray-800 font-medium">Devis personnalisé sans engagement</span>
                  </li>
                </ul>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => setSubmitted(false)} 
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
                >
                  <Send size={20} />
                  Envoyer un autre message
                </button>
                <a 
                  href="/" 
                  className="bg-gray-800 hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold text-center transition-all shadow-md hover:shadow-lg"
                >
                  Retour à l'accueil
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <SeoMeta
        title="Parlons de votre projet digital"
        description="Contactez Dev-Tech pour un diagnostic gratuit, un devis personnalisé et un accompagnement sur mesure."
        canonical="https://devtechpro.fr/contact"
        openGraph={{
          title: 'Contact Dev-Tech',
          description: 'Contactez Dev-Tech pour un accompagnement digital complet.',
          url: 'https://devtechpro.fr/contact'
        }}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          url: 'https://devtechpro.fr/contact',
          mainEntity: {
            "@type": "Organization",
            name: 'Dev-Tech',
            contactPoint: {
              "@type": 'ContactPoint',
              telephone: '+33 7 58 13 24 06',
              contactType: 'customer support',
              areaServed: 'FR'
            }
          }
        }}
      />
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-blue-800 to-purple-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Contactez-nous</h1>
            <p className="text-xl text-blue-100 mb-8 font-medium">
              Discutons de votre projet et trouvons ensemble la meilleure solution digitale.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-white/25 backdrop-blur-sm px-5 py-3 rounded-full border border-white/30">
                <Clock size={20} className="text-white" />
                <span className="font-semibold">Réponse sous 24h</span>
              </div>
              <div className="flex items-center gap-2 bg-white/25 backdrop-blur-sm px-5 py-3 rounded-full border border-white/30">
                <Check size={20} className="text-white" />
                <span className="font-semibold">Devis gratuit</span>
              </div>
              <div className="flex items-center gap-2 bg-white/25 backdrop-blur-sm px-5 py-3 rounded-full border border-white/30">
                <Globe size={20} className="text-white" />
                <span className="font-semibold">Support 5j/7</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-8 pb-4 border-b border-gray-200">
                  Nos coordonnées
                </h2>
                
                <div className="space-y-8">
                  <div className="flex gap-5 p-4 hover:bg-blue-50 rounded-xl transition-colors border border-transparent hover:border-blue-100">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 border border-blue-200">
                      <Phone size={28} className="text-blue-700" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg mb-1">Téléphone</h4>
                      <p className="text-gray-800 font-semibold text-xl mb-1">+33 7 58 13 24 06</p>
                      <p className="text-gray-600 font-medium">Lundi au vendredi, 9h-18h</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-5 p-4 hover:bg-green-50 rounded-xl transition-colors border border-transparent hover:border-green-100">
                    <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0 border border-green-200">
                      <Mail size={28} className="text-green-700" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg mb-1">Email</h4>
                      <p className="text-gray-800 font-semibold text-xl mb-1">fouad.maouchi.96@gmail.com</p>
                      <p className="text-gray-600 font-medium">Réponse sous 24h</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-5 p-4 hover:bg-purple-50 rounded-xl transition-colors border border-transparent hover:border-purple-100">
                    <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 border border-purple-200">
                      <MapPin size={28} className="text-purple-700" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg mb-1">Adresse</h4>
                      <p className="text-gray-800 font-semibold text-xl mb-1">
                        Montpellier<br />
                        34000, France
                      </p>
                      <p className="text-gray-600 font-medium">Sur rendez-vous uniquement</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-10 pt-8 border-t border-gray-200">
                  <h3 className="flex items-center gap-3 font-bold text-gray-900 mb-6 text-lg">
                    <Clock size={24} className="text-blue-700" />
                    Heures d'ouverture
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-gray-700 font-semibold">Lundi - Vendredi:</span>
                      <span className="font-bold text-gray-900 text-lg">9h00 - 18h00</span>
                    </li>
                    <li className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-gray-700 font-semibold">Samedi:</span>
                      <span className="font-bold text-gray-900 text-lg">10h00 - 14h00</span>
                    </li>
                    <li className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-gray-700 font-semibold">Dimanche:</span>
                      <span className="font-bold text-red-700 text-lg">Fermé</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-700 to-purple-700 rounded-2xl p-8 text-white shadow-lg border border-blue-600">
                <h3 className="text-2xl font-bold mb-8 pb-4 border-b border-white/20">Notre processus</h3>
                <div className="space-y-8">
                  <div className="flex gap-5">
                    <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center flex-shrink-0 border border-white/40">
                      <span className="font-bold text-white text-lg">1</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-xl mb-2">Premier contact</h4>
                      <p className="text-blue-100 font-medium">Vous nous expliquez votre projet</p>
                    </div>
                  </div>
                  <div className="flex gap-5">
                    <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center flex-shrink-0 border border-white/40">
                      <span className="font-bold text-white text-lg">2</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-xl mb-2">Analyse</h4>
                      <p className="text-blue-100 font-medium">Étude de faisabilité et devis</p>
                    </div>
                  </div>
                  <div className="flex gap-5">
                    <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center flex-shrink-0 border border-white/40">
                      <span className="font-bold text-white text-lg">3</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-xl mb-2">Lancement</h4>
                      <p className="text-blue-100 font-medium">Démarrage du projet</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
              <div className="mb-8 pb-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Envoyez-nous un message</h2>
                <p className="text-gray-800 font-semibold">
                  Tous les champs marqués d'un * sont obligatoires
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Nom complet <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3.5 rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900 placeholder:text-gray-500`}
                      placeholder="Votre nom et prénom"
                    />
                    {errors.name && (
                      <div className="flex items-center gap-2 text-red-600 text-sm mt-2 font-semibold">
                        <AlertCircle size={16} />
                        {errors.name}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Email <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3.5 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900 placeholder:text-gray-500`}
                      placeholder="votre@email.com"
                    />
                    {errors.email && (
                      <div className="flex items-center gap-2 text-red-600 text-sm mt-2 font-semibold">
                        <AlertCircle size={16} />
                        {errors.email}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3.5 rounded-lg border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900 placeholder:text-gray-500`}
                      placeholder="07 58 13 24 06"
                    />
                    {errors.phone && (
                      <div className="flex items-center gap-2 text-red-600 text-sm mt-2 font-semibold">
                        <AlertCircle size={16} />
                        {errors.phone}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Entreprise
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900 placeholder:text-gray-500"
                      placeholder="Nom de votre entreprise"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-3">
                    Sujet <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3.5 rounded-lg border ${errors.subject ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900 placeholder:text-gray-500`}
                    placeholder="Objet de votre message"
                  />
                  {errors.subject && (
                    <div className="flex items-center gap-2 text-red-600 text-sm mt-2 font-semibold">
                      <AlertCircle size={16} />
                      {errors.subject}
                    </div>
                  )}
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Type de projet
                    </label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900"
                    >
                      <option value="" className="text-gray-500">Sélectionnez un type</option>
                      <option value="website" className="text-gray-900">Site Web Vitrine</option>
                      <option value="ecommerce" className="text-gray-900">Site E-commerce</option>
                      <option value="marketing" className="text-gray-900">Marketing Digital</option>
                      <option value="print" className="text-gray-900">Design & Impression</option>
                      <option value="crm" className="text-gray-900">CRM/Gestion</option>
                      <option value="maintenance" className="text-gray-900">Maintenance</option>
                      <option value="other" className="text-gray-900">Autre projet</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-3">
                      Budget approximatif
                    </label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900"
                    >
                      <option value="" className="text-gray-500">Sélectionnez</option>
                      <option value="0-1000" className="text-gray-900">0 - 1 000 €</option>
                      <option value="1000-3000" className="text-gray-900">1 000 - 3 000 €</option>
                      <option value="3000-5000" className="text-gray-900">3 000 - 5 000 €</option>
                      <option value="5000-10000" className="text-gray-900">5 000 - 10 000 €</option>
                      <option value="10000+" className="text-gray-900">Plus de 10 000 €</option>
                      <option value="unsure" className="text-gray-900">À définir</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-3">
                    Délai souhaité
                  </label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900"
                  >
                    <option value="" className="text-gray-500">Sélectionnez</option>
                    <option value="urgent" className="text-gray-900">Urgent (moins de 1 mois)</option>
                    <option value="1-3months" className="text-gray-900">1 à 3 mois</option>
                    <option value="3-6months" className="text-gray-900">3 à 6 mois</option>
                    <option value="6+months" className="text-gray-900">Plus de 6 mois</option>
                    <option value="flexible" className="text-gray-900">Flexible</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-3">
                    Message <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    required
                    className={`w-full px-4 py-3.5 rounded-lg border ${errors.message ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900 placeholder:text-gray-500 resize-none`}
                    placeholder="Décrivez votre projet en détail..."
                  />
                  <div className="text-right text-sm text-gray-700 mt-2 font-semibold">
                    {formData.message.length} / 2000 caractères
                  </div>
                  {errors.message && (
                    <div className="flex items-center gap-2 text-red-600 text-sm mt-2 font-semibold">
                      <AlertCircle size={16} />
                      {errors.message}
                    </div>
                  )}
                </div>
                
                <div className="space-y-8">
                  <div className="flex items-start bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <input 
                      type="checkbox" 
                      id="privacy" 
                      required 
                      className="mt-1 mr-4 w-5 h-5"
                    />
                    <label htmlFor="privacy" className="text-sm text-gray-800 font-medium">
                      J'accepte que mes données soient traitées conformément à la{' '}
                      <a href="/privacy" className="text-blue-700 hover:underline font-bold">
                        politique de confidentialité
                      </a>
                    </label>
                  </div>
                  
                  <button 
                    type="submit" 
                    disabled={loading} 
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-3 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <>
                        <Send size={22} />
                        <span>Envoyer le message</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Nous trouver
          </h2>
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-10 border border-blue-200 shadow-lg">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-blue-300">
                <MapPin size={36} className="text-blue-800" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Montpellier - France</h3>
              <p className="text-gray-800 font-semibold text-xl mb-2">34000 Montpellier</p>
              <p className="text-gray-600 font-medium mb-8">Sur rendez-vous uniquement</p>
              <a 
                href="https://maps.google.com/?q=Montpellier,France"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-blue-800 hover:bg-blue-900 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg hover:shadow-xl"
              >
                <MapPin size={20} />
                Voir sur Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}